package org.example.zone360.pages.dashboard.chart

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.coroutines.delay
import org.example.zone360.theme.Palette
import org.example.zone360.utils.Res
import org.example.zone360.utils.getCurrentMilliseconds
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Canvas
import org.w3c.dom.BUTT
import org.w3c.dom.CanvasLineCap
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.ROUND
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin
import kotlin.random.Random

external class Chart(
    ctx: dynamic,
    config: dynamic
) {
    companion object {
        fun getChart(key: dynamic): Chart?
    }

    fun destroy()
}


private fun belowTargetColor(chartId: String): String {

    val normalizedId = chartId
        .removeSuffix("_popup")
        .removeSuffix("_detail")
        .substringBeforeLast("_")

    return when (normalizedId) {
        Res.Id.coachTalkGauge -> Palette.CoachTalkPrimaryBlue.hexLight
        Res.Id.sessionIntensityGauge -> Palette.SessionIntensityGreen.hexLight
        Res.Id.gameRealismGauge -> Palette.GameRealismPurple.hexLight
        Res.Id.developmentOrientationGauge -> Palette.DevelopmentOrange.hexLight
        Res.Id.tacticalComplexityGauge -> Palette.TacticalComplexityCyan.hexLight
        Res.Id.topicDiversityGauge -> Palette.TopicDiversityPink.hexLight
        Res.Id.methodologyConsistencyGauge -> Palette.MethodologyBlue.hexLight
        Res.Id.cognitiveTrainingGauge -> Palette.CognitiveTrainingGreen.hexLight
        Res.Id.competitiveEnvironmentGauge -> Palette.CompetitiveEnvironmentRed.hexLight
        Res.Id.defensiveOrgGauge -> Palette.DefensiveOrganizationRed.hexLight
        Res.Id.setPieceGauge -> Palette.SetPieceEfficiencyAmber.hexLight
        else -> Palette.CoachTalkPrimaryBlue.hexLight
    }
}
/**
 * Draws a half-circle gauge with 3 colored zones (below target / in target / above target)
 * and a needle pointing at `value`.
 */
fun drawZoneGauge(
    chartId: String,
    value: Int,
    minTarget: Int,
    maxTarget: Int,
    maxScale: Int = 100,
    canvasWidth: Int = 240,
    canvasHeight: Int = 170
) {
    val canvas = document.getElementById(chartId) as? HTMLCanvasElement ?: return

    canvas.width = canvasWidth
    canvas.height = canvasHeight

    val ctx = canvas.getContext("2d") as CanvasRenderingContext2D

    val w = canvasWidth.toDouble()
    val h = canvasHeight.toDouble()

    ctx.clearRect(0.0, 0.0, w, h)

    // --------------------------
    // Gauge Geometry
    // --------------------------

    val centerX = w / 2

    // Move center down so gauge floats like screenshot
    val centerY = h * 0.82

    val radius = 92.0

    val lineWidth = 22.0

    fun angleFor(v: Int): Double {
        val clamped = v.coerceIn(0, maxScale)
        return PI + (clamped.toDouble() / maxScale) * PI
    }

    val baseColor = belowTargetColor(chartId)

    val level1 = hexToRgba(baseColor, 0.25)
    val level2 = hexToRgba(baseColor, 0.40)
    val level3 = hexToRgba(baseColor, 0.55)
    val level4 = hexToRgba(baseColor, 0.75)
    val level5 = hexToRgba(baseColor, 1.0)

    // --------------------------
    // Gauge Arc
    // --------------------------

    ctx.lineCap = CanvasLineCap.BUTT
    ctx.lineWidth = lineWidth

    val step = maxScale / 5

    val zones = listOf(
        Triple(0, step, level1),
        Triple(step, step * 2, level2),
        Triple(step * 2, step * 3, level3),
        Triple(step * 3, step * 4, level4),
        Triple(step * 4, maxScale, level5)
    )
    zones.forEach { (from, to, color) ->

        ctx.beginPath()
        ctx.strokeStyle = color

        ctx.arc(
            centerX,
            centerY,
            radius,
            angleFor(from),
            angleFor(to)
        )

        ctx.stroke()
    }

    // White separators

    listOf(
        step,
        step * 2,
        step * 3,
        step * 4
    ).forEach { mark ->

        val angle = angleFor(mark)

        val outerRadius = radius + lineWidth / 2
        val innerRadius = radius - lineWidth / 2

        val x1 = centerX + innerRadius * cos(angle)
        val y1 = centerY + innerRadius * sin(angle)

        val x2 = centerX + outerRadius * cos(angle)
        val y2 = centerY + outerRadius * sin(angle)

        ctx.beginPath()

        ctx.strokeStyle = "#FFFFFF"
        ctx.lineWidth = 2.5

        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)

        ctx.stroke()
    }

    // Indicator Tick

    val indicatorAngle = angleFor(value)

    val startRadius = radius - lineWidth * 1.2
    val endRadius = radius - lineWidth * 0.15

    val centerStartX = centerX + startRadius * cos(indicatorAngle)
    val centerStartY = centerY + startRadius * sin(indicatorAngle)

    val centerEndX = centerX + endRadius * cos(indicatorAngle)
    val centerEndY = centerY + endRadius * sin(indicatorAngle)

// perpendicular direction
    val perp = indicatorAngle + PI / 2

    val startThickness = 3.5   // thin side
    val endThickness = 2.0     // fat side near gauge

    val p1x = centerStartX + cos(perp) * startThickness
    val p1y = centerStartY + sin(perp) * startThickness

    val p2x = centerEndX + cos(perp) * endThickness
    val p2y = centerEndY + sin(perp) * endThickness

    val p3x = centerEndX - cos(perp) * endThickness
    val p3y = centerEndY - sin(perp) * endThickness

    val p4x = centerStartX - cos(perp) * startThickness
    val p4y = centerStartY - sin(perp) * startThickness

    ctx.beginPath()

    ctx.moveTo(p1x, p1y)
    ctx.lineTo(p2x, p2y)
    ctx.lineTo(p3x, p3y)
    ctx.lineTo(p4x, p4y)

    ctx.closePath()

    ctx.fillStyle = "#111827"
    ctx.fill()
}

@Composable
fun GaugeChart(
    chartId: String,
    value: Int,
    minTarget: Int,
    maxTarget: Int,
    clubAvg: Int? = null,
    showSelection: Boolean = false,
    sessionBorder: Boolean = false,
    // Must match canvasWidth/canvasHeight in drawZoneGauge() above for crisp rendering.
    width: org.jetbrains.compose.web.css.CSSSizeValue<org.jetbrains.compose.web.css.CSSUnit.px> = if (showSelection || sessionBorder) 190.px else 240.px,
    height: org.jetbrains.compose.web.css.CSSSizeValue<org.jetbrains.compose.web.css.CSSUnit.px> = if (showSelection || sessionBorder) 140.px else 170.px
) {

    val uniqueChartId = remember(chartId) {
        "${chartId}_${getCurrentMilliseconds()}"
    }
    var animatedValue by remember { mutableStateOf(0) }

    LaunchedEffect(value) {

        val target = value.coerceIn(0, 100)

        val duration = 1200L
        val frames = 60

        repeat(frames) { frame ->

            val progress = (frame + 1).toFloat() / frames

            animatedValue = (target * progress).toInt()

            delay(duration / frames)
        }

        animatedValue = target
    }
    Box(modifier = Modifier.width(width).height(height)) {
        Canvas(
            attrs = Modifier
                .id(uniqueChartId)
                .fillMaxSize()
                .toAttrs()
        )

        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.BottomCenter
        ) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier.margin(bottom = 20.px)
            ) {
                // Main "$value%" text size — increase/decrease to taste.
                SpanText(
                    text = "$animatedValue%",
                    modifier = Modifier.fontSize(34.px).fontWeight(700)
                )
                if (clubAvg != null) {
                    SpanText(
                        text = "Club Avg: $clubAvg%",
                        modifier = Modifier.fontSize(13.px).fontWeight(600)
                    )
                }
            }
        }
    }

    LaunchedEffect(animatedValue, minTarget, maxTarget) {
        drawZoneGauge(
            chartId = uniqueChartId,
            value = animatedValue,
            minTarget = minTarget,
            maxTarget = maxTarget
        )
    }
}

fun hexToRgba(hex: String, alpha: Double): String {
    val clean = hex.trimStart('#')

    val r = clean.substring(0, 2).toInt(16)
    val g = clean.substring(2, 4).toInt(16)
    val b = clean.substring(4, 6).toInt(16)

    return "rgba($r,$g,$b,$alpha)"
}
